import { useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import PrimaryButton from "@/shared/ui/PrimaryButton";
import { OVERLAY_PANEL_MS } from "@/shared/lib/motion";
import { resolveOverlayBackAction, type OverlayBackdropReaction } from "@/shared/lib/overlayBackAction";
import { blockActivationKeys } from "@/shared/lib/overlayInput";
import { useBodyScrollLock } from "@/shared/hooks/useBodyScrollLock";
import { useDialogFocusOnOpen } from "@/shared/hooks/useDialogFocusOnOpen";
import { useOverlayBackRegistration } from "@/shared/hooks/useOverlayBackRegistration";
import { useOverlayPresence } from "@/shared/hooks/useOverlayPresence";
import styles from "./AlertDialog.module.scss";

type AlertDialogProps = {
  isOpen: boolean;
  icon?: ReactNode | string;
  title: string;
  message: string; // white-space: pre-line
  detail?: ReactNode; // 보통 <DetailList />
  confirmLabel?: string; // 기본 "확인"
  onConfirm: () => void;
  onDismiss?: () => void; // backdrop/native-back dismiss 경로 (onConfirm 과 분리)
  closeOnBackdrop?: boolean; // 기본 false
};

type SnapshotContent = {
  icon?: ReactNode | string;
  title: string;
  message: string;
  detail?: ReactNode;
  confirmLabel: string;
};

/**
 * 범용 중앙 모달 — createPortal, role="dialog", scale 0.92→1 + opacity 280ms(--overlay-panel-duration).
 * 마운트·열림·닫힘 대기는 공용 useOverlayPresence. 열려 있는 동안은 현재 prop 을, 닫히는 동안은 마지막 내용을 그린다.
 * - backdrop click: closeOnBackdrop=true 일 때만 onDismiss (onConfirm 절대 호출 금지), false 면 흔들림
 * - native back: 배경 탭과 같은 반응(B2 결정 2026-10-27) — 뒤로가기로 onConfirm 을 실행하지 않는다
 * - 뒤 화면 잠금: 공용 useBodyScrollLock(position:fixed + 위치 보존) — 열려 있는 동안, 닫히기 시작하면 해제
 */
const AlertDialog = ({
  isOpen,
  icon,
  title,
  message,
  detail,
  confirmLabel = "확인",
  onConfirm,
  onDismiss,
  closeOnBackdrop = false,
}: AlertDialogProps) => {
  const [shaking, setShaking] = useState(false);
  const [lastShown, setLastShown] = useState<SnapshotContent>({
    title: "",
    message: "",
    confirmLabel: "확인",
  });
  const baseId = useId();
  const titleId = `alert-dialog-title-${baseId}`;
  const dialogRef = useRef<HTMLDivElement>(null);
  // 배경 탭·뒤로가기의 dismiss 중복 실행 가드 — 열릴 때마다 초기화.
  const dismissingRef = useRef(false);

  // 마운트·열림 애니메이션·닫힘 대기(동작 줄이기면 즉시) — 열리는 커밋에 바로 마운트한다.
  const { shouldRender, isAnimating } = useOverlayPresence(isOpen, OVERLAY_PANEL_MS, dialogRef);

  useEffect(() => {
    if (isOpen) dismissingRef.current = false;
  }, [isOpen]);

  // 내용: 열려 있는 동안은 현재 prop, 닫히는 동안은 마지막으로 보인 내용(prop 이 먼저 비워져도 닫힘 애니메이션 동안 유지).
  // 마운트가 열리는 커밋으로 앞당겨져서, effect 로 채우는 스냅숏만 쓰면 첫 커밋에 이전 내용이 그려진다.
  useEffect(() => {
    if (isOpen) {
      setLastShown({ icon, title, message, detail, confirmLabel });
    }
  }, [isOpen, icon, title, message, detail, confirmLabel]);
  const snapshot: SnapshotContent = isOpen ? { icon, title, message, detail, confirmLabel } : lastShown;

  useBodyScrollLock(isOpen);

  // 포커스 이동으로 소프트 키보드 해제 (#216) — isOpen && shouldRender 여야
  // 닫힘 애니메이션 중 재발화하지 않으면서도 첫 렌더에서 ref 가 null 이 아니다.
  useDialogFocusOnOpen(isOpen && shouldRender, dialogRef);

  // 배경(Dim) 탭의 반응:
  // - closeOnBackdrop=true + onDismiss 있으면 dismiss (onConfirm 절대 호출 금지)
  // - closeOnBackdrop=true 인데 onDismiss 가 없으면 아무 일 없음
  // - closeOnBackdrop=false 면 닫지 않고 Spec 4 흔들림으로 강제 선택 유도
  const backdropReaction: OverlayBackdropReaction = closeOnBackdrop
    ? onDismiss
      ? "close"
      : "none"
    : "shake";

  const applyBackdropReaction = (reaction: OverlayBackdropReaction) => {
    if (reaction === "close") {
      if (dismissingRef.current) return;
      dismissingRef.current = true;
      onDismiss?.();
    } else if (reaction === "shake") {
      setShaking(true);
    }
  };

  const handleBackdropClick = () => {
    // 닫히는 동안의 배경 탭은 소비만(흔들림·재-dismiss 없음).
    if (!isOpen) return;
    applyBackdropReaction(backdropReaction);
  };

  // 확인 버튼 — 닫히는 동안(키보드 Enter 등)은 무시해 onConfirm 재실행을 막는다.
  // 열려 있는 동안의 반복 탭은 지금 동작 그대로(팝업을 닫지 않는 확인 동작이 있음).
  const handleConfirm = () => {
    if (!isOpen) return;
    onConfirm();
  };

  // native back(AOS 백버튼/iOS edge-swipe) = 배경 탭과 같게(B2 결정 2026-10-27).
  // 열린 순간부터 언마운트까지 등록해 닫히는 동안에도 뒤로가기가 아래 화면으로 새지 않게 한다(닫히는 동안은 소비만).
  // 전역 호스트처럼 닫히는 동안 다시 열리면 스택 맨 위로 다시 올라간다(useOverlayBackRegistration).
  const handleBack = () => {
    const action = resolveOverlayBackAction({
      isClosing: !isOpen || dismissingRef.current,
      backdrop: backdropReaction,
    });
    applyBackdropReaction(action);
  };
  useOverlayBackRegistration(isOpen, shouldRender, handleBack);

  if (!shouldRender) return null;

  // 텍스트 그룹 (제목 + 메시지 + 선택적 detail) — icon 유무에 따라 래핑 여부 결정
  const textContent = (
    <div className={styles.text}>
      <p id={titleId} className={styles.title}>
        {snapshot.title}
      </p>
      <p className={styles.message}>{snapshot.message}</p>
      {snapshot.detail != null && <div className={styles.detail}>{snapshot.detail}</div>}
    </div>
  );

  return createPortal(
    <div
      className={`${styles.overlay} ${isAnimating ? styles.overlayOpen : ""}`}
      role="presentation"
      onClick={handleBackdropClick}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        // 닫히는 동안은 내용 입력을 막는다(포인터는 CSS, 키보드 Enter·Space 는 캡처 단계에서) — 확인 재실행 방지.
        className={`${styles.dialog} ${isAnimating ? styles.dialogOpen : ""} ${shaking ? styles.shake : ""} ${!isOpen ? styles.contentClosing : ""}`}
        onKeyDownCapture={!isOpen ? blockActivationKeys : undefined}
        onKeyUpCapture={!isOpen ? blockActivationKeys : undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        onAnimationEnd={(e) => {
          // 자식 애니메이션 버블링 무시 — 다이얼로그 자신의 shake 종료만 처리
          if (e.target === e.currentTarget) setShaking(false);
        }}
      >
        {snapshot.icon != null ? (
          // 아이콘 있음: iconGroup 으로 아이콘 + 텍스트 묶음
          <div className={styles.iconGroup}>
            {typeof snapshot.icon === "string" ? (
              <img src={snapshot.icon} alt="" className={styles.icon} />
            ) : (
              <div className={styles.iconBox}>{snapshot.icon}</div>
            )}
            {textContent}
          </div>
        ) : (
          // 아이콘 없음 (A1): iconGroup 생략, 텍스트 그룹만
          textContent
        )}
        <div className={styles.actions}>
          {/* 버튼 콜백은 현재 prop 사용 (스냅샷 대상 아님) */}
          <PrimaryButton onClick={handleConfirm}>{snapshot.confirmLabel}</PrimaryButton>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AlertDialog;
